import {
    Button,
    Stack,
    Typography,
    Dialog as MuiDialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Alert,
} from "@mui/material";
import {Dialog} from "../base/Dialog";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {AnnouncementsTable} from "./Announcements/AnnouncementsTable";
import {
    getNotifications,
    patchNotification,
    postNotification,
    putNotification,
} from "../../services/notificationsService";
import {
    AnnouncementTranslatedContent,
    NotificationStatus,
    NotificationTargetPlatform,
    NotificationType,
} from "../../types/domain/notification";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Campaign, Edit, Warning} from "@mui/icons-material";
import AddAnnouncement from "./AdminAnnouncements/AddAnnouncement";
import {Notification} from "../../types/domain/notification";
import {CompanyMemberRole} from "../../types/domain/companyMember";
import {LANGUAGES} from "../../utils/enumItemsUtils";
import {LanguageType} from "../../types/domain/languageType";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {z, ZodError} from "zod";
import {captureException} from "@sentry/react";

export const AdminAnnouncementsView: React.FC = () => {
    const {t} = useTranslation();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [addAnnouncementDialogVisible, setAddAnnouncementDialogVisible] = useState(false);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState(false); // <-- new
    const [name, setName] = useState("");
    const [total, setTotal] = useState(0);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedCompanyMemberRoles, setSelectedCompanyMemberRoles] = useState<CompanyMemberRole[]>([]);
    const [allCompanies, setAllCompanies] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [notificationId, setNotificationId] = useState<string | undefined>();
    const [status, setStatus] = useState(NotificationStatus.DRAFT);
    const [startTime, setStartTime] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState(false);
    const [contentObject, setContentObject] = useState<Partial<Record<LanguageType, AnnouncementTranslatedContent>>>(
        {},
    );
    const [platforms, setPlatforms] = useState<Record<NotificationTargetPlatform, boolean>>({
        [NotificationTargetPlatform.WEB]: false,
        [NotificationTargetPlatform.MOBILE]: false,
    });
    const [errors, setErrors] = useState<ZodError>();

    function initContentObject() {
        const _contentObject: Partial<Record<LanguageType, AnnouncementTranslatedContent>> = {};
        LANGUAGES.forEach((language) => {
            _contentObject[language.id] = {
                shortText: "",
                content: "",
                title: "",
            };
        });
        setContentObject(_contentObject);
    }

    async function _getNotifications() {
        const data = await getNotifications({type: NotificationType.ANNOUNCEMENT});
        setNotifications(data.data);
        setTotal(parseInt(data.total));
    }

    useEffect(() => {
        initContentObject();
        void _getNotifications();
    }, []);

    function resetForm() {
        initContentObject();
        setIsEditing(false);
        setNotificationId(undefined);
        setName("");
        setSelectedCompanyIds([]);
        setSelectedCompanyMemberRoles([]);
        setAllCompanies(false);
        setStatus(NotificationStatus.DRAFT);
        setStartTime(null);
        setPlatforms({
            [NotificationTargetPlatform.WEB]: false,
            [NotificationTargetPlatform.MOBILE]: false,
        });
    }

    async function _sendAnnouncement() {
        try {
            if (isEditing && notificationId) {
                await putNotification(notificationId, {
                    name,
                    metaData: {content: contentObject},
                    startTime,
                    status,
                    targetAll: allCompanies,
                    targetCompanies: allCompanies ? undefined : selectedCompanyIds,
                    targetRoles: allCompanies ? undefined : selectedCompanyMemberRoles,
                    targetPlatforms: Object.entries(platforms)
                        .filter(([, active]) => active)
                        .map(([platform]) => platform as NotificationTargetPlatform),
                });
                setIsEditing(false);
                setNotificationId(undefined);
            } else {
                await postNotification({
                    name,
                    metaData: {content: contentObject},
                    startTime,
                    type: NotificationType.ANNOUNCEMENT,
                    status,
                    targetAll: allCompanies,
                    targetCompanies: allCompanies ? undefined : selectedCompanyIds,
                    targetRoles: allCompanies ? undefined : selectedCompanyMemberRoles,
                    targetPlatforms: Object.entries(platforms)
                        .filter(([, active]) => active)
                        .map(([platform]) => platform as NotificationTargetPlatform),
                });
            }

            setAddAnnouncementDialogVisible(false);
            resetForm();
            void _getNotifications();
        } catch (error) {
            setSubmitError(true);
            console.log(error);
            captureException(error);
        }
    }

    function processAnnouncement() {
        const result = announcementSchema.safeParse({
            name,
            allCompanies,
            selectedCompanyIds,
            selectedCompanyMemberRoles,
            platforms,
        });

        if (!result.success) {
            setErrors(result.error);
            return;
        }

        setErrors(undefined);

        if (import.meta.env.PROD) {
            setConfirmDialogVisible(true);
        } else {
            void _sendAnnouncement();
        }
    }

    function confirmSend() {
        setConfirmDialogVisible(false);
        void _sendAnnouncement();
    }

    function cancelSend() {
        setConfirmDialogVisible(false);
    }

    function editAnnouncement(announcement: Notification) {
        if (announcement?.id) setNotificationId(announcement.id);
        setIsEditing(true);
        setName(announcement.name);
        setAllCompanies(!!announcement.targetAll);
        if (announcement?.metaData?.content) setContentObject(announcement.metaData.content);
        if (announcement.targetCompanies?.length) setSelectedCompanyIds(announcement.targetCompanies);
        if (announcement.targetRoles?.length) setSelectedCompanyMemberRoles(announcement.targetRoles);
        if (announcement.startTime?.length) setStartTime(announcement.startTime);
        if (announcement.status?.length) setStatus(announcement.status);
        if (announcement.targetPlatforms?.length) {
            setPlatforms({
                [NotificationTargetPlatform.WEB]: announcement.targetPlatforms.includes(NotificationTargetPlatform.WEB),
                [NotificationTargetPlatform.MOBILE]: announcement.targetPlatforms.includes(
                    NotificationTargetPlatform.MOBILE,
                ),
            });
        }
        setAddAnnouncementDialogVisible(true);
    }

    async function changeNotificationStatus(id: string, status: NotificationStatus) {
        setNotifications(
            notifications.map((notification) => {
                if (notification.id === id) notification.status = status;
                return notification;
            }),
        );
        await patchNotification(id, status);
    }

    const announcementSchema = z
        .object({
            name: z.string().min(3, {message: t("announcements_page.validation_name")}),
            allCompanies: z.boolean(),
            selectedCompanyIds: z.array(z.string()),
            selectedCompanyMemberRoles: z.array(z.string()),
            platforms: z.object({
                [NotificationTargetPlatform.MOBILE]: z.boolean().default(false),
                [NotificationTargetPlatform.WEB]: z.boolean().default(false),
            }),
        })
        .superRefine((data, ctx) => {
            const hasCompany = data.selectedCompanyIds.length > 0;
            const hasRole = data.selectedCompanyMemberRoles.length > 0;

            if (!data.allCompanies && !hasCompany && !hasRole) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("announcements_page.validation_audience"),
                    path: ["audience"],
                });
            }

            const anyPlatformSelected = Object.values(data.platforms).some((v) => v);
            if (!anyPlatformSelected) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("announcements_page.validation_platforms"),
                    path: ["platforms"],
                });
            }
        });

    return (
        <Stack direction="column" sx={{padding: "1.125rem"}} overflow="hidden" gap="1.5rem">
            <Stack>
                <Typography
                    fontSize={22}
                    display={"flex"}
                    alignItems="center"
                    sx={{textTransform: "uppercase"}}
                    color="primary"
                >
                    <Campaign sx={{marginRight: "12px"}} />
                    {t("announcements_page.announcements")}
                    <Button
                        sx={{whiteSpace: "nowrap", marginLeft: "auto"}}
                        startIcon={<AddIcon />}
                        onClick={() => setAddAnnouncementDialogVisible(true)}
                    >
                        {t("announcements_page.add_announcement")}
                    </Button>
                </Typography>
            </Stack>
            <Stack>
                <AnnouncementsTable
                    announcements={notifications}
                    total={total}
                    page={1}
                    rowsPerPage={10}
                    editAnnouncement={editAnnouncement}
                    setAnnouncements={setNotifications}
                    changeNotificationStatus={changeNotificationStatus}
                />
            </Stack>

            <Dialog
                title={t("announcements_page.add_announcement")}
                titleIcon={<Edit />}
                open={addAnnouncementDialogVisible}
                onClose={() => {
                    setAddAnnouncementDialogVisible(false);
                    resetForm();
                }}
                submitTitle={
                    status === NotificationStatus.SENT || status === NotificationStatus.SCHEDULED
                        ? t("announcements_page.form_send")
                        : t("announcements_page.form_save")
                }
                enableSubmit={true}
                onSubmit={processAnnouncement}
                minWidth={"600px"}
                maxWidth={"1200px"}
                maxHeight={"90%"}
                minHeight={"840px"}
            >
                <AddAnnouncement
                    name={name}
                    setName={setName}
                    setSelectedCompanyIds={setSelectedCompanyIds}
                    selectedCompanyIds={selectedCompanyIds}
                    setSelectedCompanyMemberRoles={setSelectedCompanyMemberRoles}
                    selectedCompanyMemberRoles={selectedCompanyMemberRoles}
                    setAllCompanies={setAllCompanies}
                    allCompanies={allCompanies}
                    status={status}
                    setStatus={setStatus}
                    setStartTime={setStartTime}
                    startTime={startTime}
                    setContentObject={setContentObject}
                    contentObject={contentObject}
                    platforms={platforms}
                    setPlatforms={setPlatforms}
                    errors={errors}
                    submitError={submitError}
                />
            </Dialog>

            <MuiDialog open={confirmDialogVisible} onClose={cancelSend}>
                <DialogContent>
                    <DialogContentText>
                        <div style={{display: "flex"}}>
                            <Warning sx={{color: "orange"}} fontSize={"medium"} />
                            &nbsp;<b>{t("announcements_page.warning_dialog_title")}</b>
                        </div>
                        <br />
                        {t("announcements_page.warning_dialog_content")} <br />
                        <br />
                        <Alert icon={<InfoIcon fontSize="inherit" />} severity="info" sx={{mt: 2}}>
                            {t("announcements_page.warning_dialog_info")}
                        </Alert>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelSend} color="primary">
                        {t("announcements_page.warning_dialog_cancel")}
                    </Button>
                    <Button onClick={confirmSend} color="primary" variant="contained">
                        {t("announcements_page.warning_dialog_send")}
                    </Button>
                </DialogActions>
            </MuiDialog>
        </Stack>
    );
};
